import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
	type KeyboardEvent,
	type MouseEvent,
} from 'react';
import { styled } from '@compiled/react';
// TODO: JFP-2824 | Suppressed to enable upgrade to ESLint v9 - please fix this if you can!
// eslint-disable-next-line jira/restricted/@atlaskit+analytics-next
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { SmartCardProviderMigration } from '@atlassian/jira-linking-platform-provider-migration/src/index.tsx';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { useRovoEntitlementResource } from '@atlassian/jira-router-resources-rovo-entitlement/src/services/index.tsx';
/**
 * Components required to compose the extensible cross product search dialog
 */
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useLegacySearchTheme } from '@atlassian/navigation-system/experimental/use-legacy-search-theme';
import {
	BitbucketTab,
	CompassTab,
	ConfluenceFilterContextProvider,
	ExtensibleConfluenceTab,
	ExtensibleJiraTab,
	JiraFilterContextProvider,
	MultiProductDialog,
	OpsgenieTab,
	TownsquareTab,
	TrelloTab,
	UserInputContextProvider,
	ConfluenceUpsellInJiraTab,
	CrossProductTab,
} from '@atlassian/product-search-dialog';
import type { LinkComponentProps } from '@atlassian/search-dialog';
import { useNINGlobalScopeEnrolment } from '../../../services/use-nin-global-scope-enrolment/index.tsx';
import DummyDialog from './dummy-dialog.tsx';
import type { ExtensibleDialogProps } from './types.tsx';

const TOWNSQUARE_SMARTS_PRODUCT_IDENTIFIER = 'watermelon';
const COMPASS_PRODUCT_IDENTIFIER = 'compass';
const COLLABORATION_GRAPH_URL = '/gateway/api/collaboration';
const ANALYTICS_CONTEXT_LOCATION = 'extensibleJiraTab';
export const JIRA_SCOPES = ['jira.issue', 'jira.project,board,plan,filter', 'urs.user'];

export const useInstanceInfoService = () => {
	const request = useCallback(() => performGetRequest('/rest/api/3/serverInfo'), []);

	return useService<{ displayUrlConfluence: string }>(request);
};

const jiraLinkComponent =
	(onJiraNavigation: (href: string, event: KeyboardEvent | MouseEvent) => void) =>
	(linkProps: LinkComponentProps) => {
		const { href } = linkProps;
		const onClick = (event: KeyboardEvent | MouseEvent) => {
			if (!(event.ctrlKey || event.metaKey)) {
				onJiraNavigation(href || '', event);
			}
		};

		return (
			<LinkComponent onClick={onClick} role="none" {...linkProps}>
				{linkProps.children}
			</LinkComponent>
		);
	};

const PreviewWrapper = ({ children }: { children: React.ReactElement }) => (
	<AnalyticsContext
		data={{
			attributes: { location: ANALYTICS_CONTEXT_LOCATION },
		}}
	>
		<SmartCardProviderMigration>{children}</SmartCardProviderMigration>
	</AnalyticsContext>
);

type ExtensibleCrossProductSearchDialogWithDataProps = ExtensibleDialogProps & {
	onRetry: () => void;
};

const ExtensibleCrossProductSearchDialogWithData = (
	props: ExtensibleCrossProductSearchDialogWithDataProps,
) => {
	const rovoEntitlement = useRovoEntitlementResource();
	const isRovoEnabled = rovoEntitlement?.data?.isRovoEnabled ?? false;

	const {
		cloudId,
		formatDate,
		isExpanded,
		jiraFeatures,
		onNavigate,
		onClose,
		onRetry,
		confluenceReactivationExperiment,
		confluenceAvailabilityExperiment,
	} = props;

	const instanceInfoResponse = useInstanceInfoService();
	const { fetch: fetchInstanceInfo } = instanceInfoResponse;

	useEffect(() => {
		if (fg('search_dialog_fetch_server_info')) {
			fetchInstanceInfo();
		}
	}, [fetchInstanceInfo]);

	const onJiraNavigation = useCallback(
		(href: string, event: KeyboardEvent | MouseEvent) => {
			onNavigate(href, event);
			onClose();
		},
		[onClose, onNavigate],
	);

	const JiraLinkComponent = useMemo(() => jiraLinkComponent(onJiraNavigation), [onJiraNavigation]);
	const { data: orgId } = useOrgId();
	const projectData = useProject();

	const { user } = useCurrentUser().data;
	const userDetails = useMemo(
		() =>
			user && !user.isAnonymous && user.accountId
				? {
						id: user.accountId,
						name: user.userFullname,
						// @ts-expect-error - TS2339 - Property 'avatarUrls' does not exist on type 'DataBasic | (DataBasic & ResponseSuccess)'. | TS2339 - Property 'avatarUrls' does not exist on type 'DataBasic | (DataBasic & ResponseSuccess)'.
						avatarUrl: user.avatarUrls ? user.avatarUrls['48x48'] : '',
					}
				: undefined,
		[user],
	);

	const licensedProducts = useLicensedProducts();

	const isSearchWithTextFieldInAllIssuesLinkEnabled = !useNINGlobalScopeEnrolment({
		cloudId,
		isExpanded,
	});

	return (
		<>
			{isRovoEnabled && fg('jira_rovo_in_quick_search') && (
				<CrossProductTab order={-1} onRetry={onRetry} />
			)}
			<PreviewWrapper>
				<ExtensibleJiraTab
					onRetry={onRetry}
					permissionSupplier={() => Promise.resolve(JIRA_SCOPES)}
					features={{
						...jiraFeatures,
						isSearchWithTextFieldInAllIssuesLinkEnabled,
					}}
					order={0}
					isPrefetchingEnabled
					linkComponent={JiraLinkComponent}
					config={{
						cloudId,
						collaborationGraphUrl: COLLABORATION_GRAPH_URL,
					}}
					currentProject={
						projectData.data
							? {
									id: projectData.data.id.toString(),
									label: projectData.data.name,
									avatarUrl: projectData.data.avatarUrl,
								}
							: undefined
					}
					currentUser={userDetails}
					autocorrectEnabled
				/>
			</PreviewWrapper>
			{confluenceAvailabilityExperiment?.isEligible && (
				<ConfluenceUpsellInJiraTab
					formatDate={formatDate}
					onRetry={onRetry}
					recentClientConfig={{
						url: instanceInfoResponse.data?.displayUrlConfluence
							? `${instanceInfoResponse.data.displayUrlConfluence}/wiki`
							: '/wiki',
						cloudId,
						isUserAnonymous: !user.accountId,
						isCollaborationGraphEnabled: false,
						collaborationGraphUrl: COLLABORATION_GRAPH_URL,
					}}
					order={1}
					confluenceAvailabilityExperiment={confluenceAvailabilityExperiment}
				/>
			)}
			{!confluenceAvailabilityExperiment?.isEligible && (
				<ExtensibleConfluenceTab
					formatDate={formatDate}
					onRetry={onRetry}
					recentClientConfig={{
						url: instanceInfoResponse.data?.displayUrlConfluence
							? `${instanceInfoResponse.data.displayUrlConfluence}/wiki`
							: '/wiki',
						cloudId,
						isUserAnonymous: !user.accountId,
						isCollaborationGraphEnabled: false,
						collaborationGraphUrl: COLLABORATION_GRAPH_URL,
					}}
					order={1}
					confluenceReactivationExperiment={confluenceReactivationExperiment}
					confluenceAvailabilityExperiment={confluenceAvailabilityExperiment}
				/>
			)}
			<BitbucketTab
				order={2}
				onRetry={onRetry}
				features={{
					enableSingleScopesCall: true,
				}}
			/>
			<CompassTab
				crossFlowEnabled
				sourceContext="jira"
				isCompassActivated={licensedProducts?.compass ?? false}
				isUserAnonymous={user.isAnonymous}
				order={3}
				onRetry={onRetry}
				userTeamConfig={{
					context: {
						contextType: 'filter',
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						organizationId: orgId!,
						principalId: 'Context',
						productKey: COMPASS_PRODUCT_IDENTIFIER,
						siteId: cloudId,
					},
				}}
			/>
			<OpsgenieTab order={4} onRetry={onRetry} cloudId={cloudId} />
			<TownsquareTab
				order={5}
				onRetry={onRetry}
				userTeamConfig={{
					context: {
						contextType: 'filter',
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						organizationId: orgId!,
						principalId: 'Context',
						productKey: TOWNSQUARE_SMARTS_PRODUCT_IDENTIFIER,
						siteId: cloudId,
					},
				}}
				// @ts-expect-error - TS2322 - Type '{ order: number; onRetry: () => void; userTeamConfig: { context: { contextType: string; principalId: string; productKey: string; siteId: string; }; }; cloudId: string; }' is not assignable to type 'IntrinsicAttributes & ExtensibleTabProps & { userTeamConfig?: UserTeamConfig | undefined; } & { children?: ReactNode; }'.
				cloudId={cloudId}
			/>
			<TrelloTab order={5} onRetry={onRetry} />
		</>
	);
};

export const ExtensibleCrossProductSearchDialog = (props: ExtensibleDialogProps) => {
	const {
		cloudId,
		aggregatorUrl,
		isExpanded,
		forwardRef,
		onNavigate,
		onOpen,
		onClose,
		theme,
		pageLoadMetrics,
		dialogFeatures,
		confluenceReactivationExperiment,
		confluenceAvailabilityExperiment,
		primaryProduct,
	} = props;

	// We want to track if the dialog has been expanded so that we do not unmount the tabs  when the dialog is collapsed
	const [hasBeenExpanded, setHasBeenExpanded] = useState(false);

	const rovoEntitlement = useRovoEntitlementResource();
	const isRovoEnabled = rovoEntitlement?.data?.isRovoEnabled ?? false;

	const { data: orgId } = useOrgId();

	const secondaryProductNavigate = (href: string) => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.location.assign(href);
	};
	const onJiraNavigation = useCallback(
		(href: string, event: KeyboardEvent | MouseEvent) => {
			onNavigate(href, event);
			onClose();
		},
		[onClose, onNavigate],
	);
	const onNavigateMultiProduct = (id: string, href: string, event: KeyboardEvent | MouseEvent) => {
		switch (id) {
			case 'jira': {
				onJiraNavigation(href, event);
				break;
			}
			default:
				secondaryProductNavigate(href);
		}
	};

	/**
	 * A nav4 search theme that works with the nav3 theming API
	 */
	const newTheme = useLegacySearchTheme();

	const setIsExpanded = useCallback(
		(value: boolean) => {
			if (value) {
				onOpen();
			} else {
				onClose();
			}
			if (value && !hasBeenExpanded) {
				setHasBeenExpanded(true);
			}
		},
		[hasBeenExpanded, onClose, onOpen],
	);

	return (
		<UserInputContextProvider stickySearchEnabled isExpanded={isExpanded}>
			<ConfluenceFilterContextProvider isEnabled={isExpanded}>
				<JiraFilterContextProvider isEnabled={isExpanded}>
					<MultiProductDialog
						shouldFillContainer={getWillShowNav4()}
						aggregatorUrl={aggregatorUrl}
						abTestCloudId={cloudId}
						isExpanded={isExpanded}
						setIsExpanded={setIsExpanded}
						theme={getWillShowNav4() ? newTheme : theme}
						// @ts-expect-error - TS2322 - Type 'HTMLInputElementRef' is not assignable to type 'Ref<HTMLInputElement>'.
						forwardRef={forwardRef}
						// @ts-expect-error - TS2322 - Type '(id: string, href: string, event: KeyboardEvent | MouseEvent) => void' is not assignable to type '(productId: string, href: string, event: MouseEvent<Element, MouseEvent> | KeyboardEvent) => void'.
						onNavigateGeneric={onNavigateMultiProduct}
						pageLoadMetrics={pageLoadMetrics}
						dialogFeatures={dialogFeatures}
						isRovoEnabled={isRovoEnabled}
						orgId={orgId}
						primaryProduct={primaryProduct}
					>
						{({ onRetry }) => {
							// For performance reasons we want to avoid rendering the dialog content when it's not expanded. However, if we don't render any child tabs
							// then the search component will not be interactive. To work around this we render a dummy dialog when the dialog is not expanded with a single
							// section that is guaranteed to included in the allowed list.
							if (isExpanded || hasBeenExpanded) {
								if (fg('blu-7149-search-dialog-tabpanel-semantics')) {
									return (
										<div role="tabpanel">
											<ExtensibleCrossProductSearchDialogWithData
												{...props}
												onRetry={onRetry}
												confluenceReactivationExperiment={confluenceReactivationExperiment}
												confluenceAvailabilityExperiment={confluenceAvailabilityExperiment}
											/>
										</div>
									);
								}
								return (
									<ExtensibleCrossProductSearchDialogWithData
										{...props}
										onRetry={onRetry}
										confluenceReactivationExperiment={confluenceReactivationExperiment}
										confluenceAvailabilityExperiment={confluenceAvailabilityExperiment}
									/>
								);
							}
							return <DummyDialog />;
						}}
					</MultiProductDialog>
				</JiraFilterContextProvider>
			</ConfluenceFilterContextProvider>
		</UserInputContextProvider>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkComponent = styled.a({
	'&:active, &:hover': {
		outline: 'none',
	},
});
